import AccordionWizardTemplate from '../components/AccordionWizardTemplate';
import { useMemo, useState } from 'react';
import { useRecoilValue, useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil';
import { useSaveAllEdi } from '../../../recoil/hooks/useSaveAllEdi';
import { EDICancellationExternalCase } from '../components/EDICancellationExternalCase';
import { externalCasesAtom } from '../../../recoil/atoms/externalCasesAtom';
import { type ExternalCaseStatus_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';
import { CheckboxField, Grid, Link } from '@flos/react-ui';
import StringParagraph from '../components/StringParagraph';
import ViewInformationModal from '../../../shared_components/ViewInformationModal/ViewInformationModal';
import { allCoverablesAtom } from '../../../recoil/atoms/allCoverablesAtom';
import { issuedCoverablesAndPricesAtom } from '../../../recoil/atoms/issuedCoverablesAndPricesAtom';
import {
    allCoverablesToCoverableDTOArray,
    isUnsupportedByEdi
} from '../../../utils/coverablesUtil';
import {
    dateHasValidationErrors,
    externalCaseToCoverable,
    filterAccEDICasesForOtherPerson,
    isCarWithoutLicensePlates,
    getPetYoungerThan8WeeksName
} from '../../../utils/externalCasesUtil';
import { useHippoText } from '../../../hooks/useHippoText';
import DifferentPolicyHolderModal from '../components/DifferentPolicyHolderModal';
import { quoteApprovedTimestampSelector } from '../../../recoil/selectors/quoteApprovedTimestampSelector';
import { addMonths, convertLocalDateDTOToDate } from '../../../utils/dateUtil';
import { jobInfoAtom } from '../../../recoil/atoms/jobInfoAtom';
import { useSaveChaConsent } from '../../../hooks/useSaveChaConsent';
import { type UpdateChaConsentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/common';
import { isTheAccordionTheLastStep, ACCORDION_STEPS } from '../configs/CheckoutAccordionConfig';
import { useIssueInsurance } from '../../../hooks/useIssueInsurance';
import useWizardNavigation from '../../../hooks/useWizardNavigation';
import EffectiveDateExternalCaseNotSupported from '../components/EffectiveDateExternalCaseNotSupported';
import { accountHolderAtom } from '../../../recoil/atoms/accountHolderAtom';
import { startDateAtom } from '../../../recoil/atoms/startDateAtom';
import type { CoverableDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { track, trackPurchase } from '../../../utils/trackingUtil';
import { refreshCoverablesSelectorAtom } from '../../../recoil/atoms/refreshCoverablesSelectorAtom';
import { containsAddCoverageChangeSelector } from '../../../recoil/selectors/containsAddCoverageSelector';
import ViewHippoInformationModal from '../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import { notUpdatedCoverablesAtom } from '../../../recoil/atoms/notUpdatedCoverablesAtom';
import CallMeModal from '../../../shared_components/CallMeModal/CallMeModal';
import type { ExternaleCaseDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/policyjob/edi/dto/';
import { isOpcJobSelector } from '../../../recoil/selectors/isOpcJobSelector';

export const EdiCaseStatusMap: Record<string, ExternalCaseStatus_Top> = {
    Draft: 'CAN_101',
    NotRequired: 'CAN_301',
    ReadyForSend: 'CAN_102',
    CreationSent: 'CAN_401',
    PendingCompletion: 'CAN_201'
};

const viewInformationModalContent = {
    title: 'Det siger du ja til',
    description: '[Insert copy from Marketing/legal]'
};

export interface LobStatusFieldsValidation {
    isStatusValid: boolean | undefined;
    isOtherCompanyValid: boolean | undefined;
    isCancellationDateValid: boolean | undefined;
    isStartDateValid: boolean | undefined;
}

interface FieldValidationFlags {
    lobStatus: Record<string, LobStatusFieldsValidation>;
    isEffDateExtCaseNotSupportedIsValid: undefined | boolean;
    isConsentFieldIsValid: undefined | boolean;
    isConsentField2IsValid: undefined | boolean;
}

const DatesAndCancellationStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const { saveData, isWaiting } = useSaveAllEdi();
    const { saveChaConsent } = useSaveChaConsent();
    const [showModal, setShowModal] = useState(false);
    const [isConsentChecked, setIsConsentChecked] = useState<boolean>(false);
    const [isConsentChecked2, setIsConsentChecked2] = useState<boolean>(false);
    const [showConsentModal, setShowConsentModal] = useState<boolean>(false);
    const [showConsent2Modal, setShowConsent2Modal] = useState<boolean>(false);
    const [showMissingLicensePlatesModal, setShowMissingLicensePlatesModal] =
        useState<boolean>(false);
    const [youngPet, setYoungPet] = useState<string>();
    const [showDifferentPolicyHolderModal, setShowDifferentPolicyHolderModal] = useState(false);
    const getHippoText = useHippoText();
    const { jobNumber } = useRecoilValue(jobInfoAtom);
    const coverables = useRecoilValue(allCoverablesAtom);
    const notUpdatedCoverables = useRecoilValue(notUpdatedCoverablesAtom);

    const allCoverables = allCoverablesToCoverableDTOArray(coverables);
    const coverablesWithoutEdiSupport = allCoverables.filter((cov) => isUnsupportedByEdi(cov));
    const startDateDTOs = useRecoilValue(startDateAtom);
    const containsAddCoverageChange = useRecoilValue(containsAddCoverageChangeSelector);
    const getStartDate = (): Date | undefined => {
        // CHO is the only LOB unsupported by EDI
        // the approach assumes CHO only have 1 coverable for a job (at time of writing)
        const coverable: CoverableDTO = coverablesWithoutEdiSupport[0];
        const startDateDTO = startDateDTOs?.find((dto) => dto.fixedId === coverable.fixedId);
        if (startDateDTO?.effectiveDate) {
            return convertLocalDateDTOToDate(startDateDTO.effectiveDate);
        }
        return undefined;
    };
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(getStartDate());
    const externalCaseDTOList = filterAccEDICasesForOtherPerson(
        useRecoilValue(externalCasesAtom),
        allCoverables
    );
    const approvalDate = useRecoilValue(quoteApprovedTimestampSelector);
    const maxSelectablePolicyChangeDate = addMonths(convertLocalDateDTOToDate(approvalDate), 13);
    const { isPolicyChange } = useRecoilValue(jobInfoAtom);
    const [accountHolderData] = useRecoilState(accountHolderAtom);
    const refreshCoverableAndPricesSelectors = useSetRecoilState(issuedCoverablesAndPricesAtom);
    const { isCVR } = accountHolderData;
    const isConsentRequired = externalCaseDTOList.some(
        (v) =>
            v.status === EdiCaseStatusMap.ReadyForSend ||
            v.status === EdiCaseStatusMap.PendingCompletion
    );

    const { wizardGotoStep } = useWizardNavigation();
    const externalCasesData = useRecoilValue(externalCasesAtom);
    const { issueInsurance } = useIssueInsurance();
    const [isLoading, setIsLoading] = useState(false);

    const setRefreshCoverablesSelector = useSetRecoilState(refreshCoverablesSelectorAtom);
    const resetCoverables = useResetRecoilState(allCoverablesAtom);
    const [fieldValidationFlags, setFieldValidationFlags] = useState<FieldValidationFlags>({
        isEffDateExtCaseNotSupportedIsValid: undefined,
        isConsentFieldIsValid: undefined,
        isConsentField2IsValid: undefined,
        lobStatus: {}
    });
    const isOpcJob = useRecoilValue(isOpcJobSelector);

    const goNextWrapper = (): void => {
        trackEdiCases();
        resetCoverables();
        setRefreshCoverablesSelector({});
        if (
            isTheAccordionTheLastStep(
                coverables,
                externalCasesData,
                isPolicyChange,
                isOpcJob,
                ACCORDION_STEPS.DATES_AND_CANCELLATION,
                isCVR,
                containsAddCoverageChange,
                notUpdatedCoverables
            )
        ) {
            setIsLoading(true);
            void issueInsurance().then(
                () => {
                    trackPurchase(allCoverables, jobNumber);
                    refreshCoverableAndPricesSelectors({});
                    wizardGotoStep('ReceiptPage');
                },
                () => {
                    setIsLoading(false);
                }
            );
        } else {
            goNext();
        }
    };

    const trackEdiCases = (): void => {
        const trackedCases = externalCasesData.map((caseItem, index) => {
            const ediCaseNumber = index + 1;
            const effectiveDate =
                caseItem.status === EdiCaseStatusMap.NotRequired
                    ? caseItem.startDate
                    : caseItem.cancellationDate;

            return {
                [`edi-${ediCaseNumber}`]:
                    caseItem.status === EdiCaseStatusMap.NotRequired ? 'no' : 'yes',
                [`existingInsuranceCompany-${ediCaseNumber}`]: caseItem.otherCompany?.name ?? 'no',
                [`effectiveDate-${ediCaseNumber}`]: effectiveDate
            };
        });

        track('EDI cancellation step', trackedCases);
    };

    const formValidation = useMemo(() => {
        const validationResults = externalCaseDTOList.map((externalCase) => {
            const { status, otherCompany, insuredIsAccountHolder, cancellationDate, startDate } =
                externalCase;
            if (status === EdiCaseStatusMap.Draft) return false;
            if (
                status === EdiCaseStatusMap.ReadyForSend ||
                status === EdiCaseStatusMap.PendingCompletion
            ) {
                if (Object.keys(otherCompany ?? {}).length === 0) return false;
                if (insuredIsAccountHolder === false) return true;
                if (
                    !cancellationDate ||
                    dateHasValidationErrors(
                        cancellationDate,
                        externalCaseToCoverable(externalCase, allCoverables),
                        allCoverables,
                        false
                    )
                ) {
                    return false;
                }
            }
            if (status === EdiCaseStatusMap.NotRequired) {
                if (
                    !startDate ||
                    dateHasValidationErrors(
                        startDate,
                        externalCaseToCoverable(externalCase, allCoverables),
                        allCoverables,
                        true
                    )
                ) {
                    return false;
                }
            }

            if (isPolicyChange && externalCase.startDate) {
                const start = convertLocalDateDTOToDate({
                    ...approvalDate,
                    hours_Top: 0,
                    minutes_Top: 0
                });
                const end = maxSelectablePolicyChangeDate;
                const selectedDate = convertLocalDateDTOToDate(externalCase.startDate);

                return selectedDate >= start && selectedDate <= end;
            }

            return true;
        });

        return validationResults.every((result) => result);
    }, [
        externalCaseDTOList,
        isPolicyChange,
        allCoverables,
        approvalDate,
        maxSelectablePolicyChangeDate
    ]);

    const goNextOrShowModal = (): void => {
        const isDifferentPolicyHolder = externalCaseDTOList.some((externalCase) => {
            const isPolicyHolder = isCVR || (externalCase.insuredIsAccountHolder ?? true);
            return (
                (externalCase.status === EdiCaseStatusMap.ReadyForSend ||
                    externalCase.status === EdiCaseStatusMap.PendingCompletion) &&
                !isPolicyHolder
            );
        });

        if (isDifferentPolicyHolder) {
            setShowDifferentPolicyHolderModal(true);
            return;
        }

        const aYoungPet = getPetYoungerThan8WeeksName(externalCaseDTOList, allCoverables);
        if (aYoungPet) {
            setYoungPet(aYoungPet);
            return;
        }

        if (isCarWithoutLicensePlates(externalCaseDTOList, allCoverables)) {
            setShowMissingLicensePlatesModal(true);
            return;
        }

        if (!isConsentRequired) {
            goNextWrapper();
            return;
        }

        const updateChaConsentDto: UpdateChaConsentDTO = {
            productLineCodes: [],
            companyVirNumbers: []
        };

        externalCaseDTOList.forEach((cov) => {
            if (cov.otherCompany?.code && cov.lobCode) {
                updateChaConsentDto.productLineCodes.push(cov.lobCode.substring(0, 3));
                updateChaConsentDto.companyVirNumbers.push(cov.otherCompany.code);
            }
        });

        setIsLoading(true);
        void saveChaConsent(updateChaConsentDto).then(
            () => {
                goNextWrapper();
            },
            () => {
                setIsLoading(false);
            }
        );
    };

    const getOtherFieldsStatus = (): {
        isEffDateExtCaseValid: boolean;
        isConsentValid: boolean;
        isConsent2Valid: boolean;
    } => {
        const isEffDateExtCaseValid = !(
            coverablesWithoutEdiSupport.length > 0 && selectedDate !== undefined
        );
        const isConsentValid = isConsentRequired && isConsentChecked;
        const isConsent2Valid = isConsentRequired && isConsentChecked2;
        return { isEffDateExtCaseValid, isConsentValid, isConsent2Valid };
    };

    const getCaseStatus = (
        externalCase: ExternaleCaseDTO
    ): {
        isStatusValid: boolean;
        isCancellationDateValid: boolean;
        isStartDateValid: boolean;
        isOtherCompanyValid: boolean;
    } => {
        const isStatusValid = [
            EdiCaseStatusMap.PendingCompletion,
            EdiCaseStatusMap.ReadyForSend,
            EdiCaseStatusMap.NotRequired
        ].includes(externalCase.status as ExternalCaseStatus_Top);
        let isOtherCompanyValid = true;
        let isCancellationDateValid = true;
        let isStartDateValid = true;
        if (
            externalCase.status === EdiCaseStatusMap.ReadyForSend ||
            externalCase.status === EdiCaseStatusMap.PendingCompletion
        ) {
            isOtherCompanyValid = !!externalCase.otherCompany;
            if (
                !externalCase.cancellationDate ||
                dateHasValidationErrors(
                    externalCase.cancellationDate,
                    externalCaseToCoverable(externalCase, allCoverables),
                    allCoverables,
                    false
                )
            ) {
                isCancellationDateValid = false;
            }
        }
        if (externalCase.status === EdiCaseStatusMap.NotRequired) {
            if (
                !externalCase.startDate ||
                dateHasValidationErrors(
                    externalCase.startDate,
                    externalCaseToCoverable(externalCase, allCoverables),
                    allCoverables,
                    true
                )
            ) {
                isStartDateValid = false;
            }
        }
        if (isPolicyChange && externalCase.startDate) {
            const start = convertLocalDateDTOToDate({
                ...approvalDate,
                hours_Top: 0,
                minutes_Top: 0
            });
            const end = maxSelectablePolicyChangeDate;
            const selectedDate = convertLocalDateDTOToDate(externalCase.startDate);

            isStartDateValid = selectedDate >= start && selectedDate <= end;
        }
        return { isStatusValid, isCancellationDateValid, isStartDateValid, isOtherCompanyValid };
    };

    const validateStatus = (): void => {
        const { isEffDateExtCaseValid, isConsentValid, isConsent2Valid } = getOtherFieldsStatus();
        let lobStatus = {};
        externalCaseDTOList.forEach((externalCase) => {
            const { linkedEntityId } = externalCase;
            const {
                isStatusValid,
                isCancellationDateValid,
                isStartDateValid,
                isOtherCompanyValid
            } = getCaseStatus(externalCase);
            lobStatus = {
                ...lobStatus,
                [linkedEntityId as string]: {
                    isStatusValid,
                    isOtherCompanyValid,
                    isCancellationDateValid,
                    isStartDateValid
                }
            };
        });

        setFieldValidationFlags({
            ...fieldValidationFlags,
            isConsentFieldIsValid: isConsentValid,
            isConsentField2IsValid: isConsent2Valid,
            isEffDateExtCaseNotSupportedIsValid: isEffDateExtCaseValid,
            lobStatus
        });
    };

    const handleGoNext = (): boolean => {
        if (
            !(formValidation && (!isConsentRequired || (isConsentChecked && isConsentChecked2))) ||
            (coverablesWithoutEdiSupport.length > 0 && selectedDate === undefined)
        ) {
            validateStatus();
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return false;
        }
        return true;
    };
    return (
        <AccordionWizardTemplate
            goNext={() => {
                if (handleGoNext()) {
                    void saveData().then(goNextOrShowModal);
                }
            }}
            isNextBtnLoading={isWaiting || isLoading}
        >
            <Grid gap={6}>
                {externalCaseDTOList.map((externalCase) => {
                    return (
                        <EDICancellationExternalCase
                            externalCase={externalCase}
                            key={externalCase.caseId}
                            lobStatusFieldValidation={
                                fieldValidationFlags.lobStatus?.[
                                    String(externalCase.linkedEntityId)
                                ]
                            }
                        />
                    );
                })}
            </Grid>
            {coverablesWithoutEdiSupport.map((coverable) => {
                return (
                    <EffectiveDateExternalCaseNotSupported
                        coverables={coverable}
                        selectedDate={selectedDate}
                        setSelectedDate={setSelectedDate}
                        key={coverable.fixedId}
                        isEffDateExtCaseNotSupportedIsValid={
                            fieldValidationFlags.isEffDateExtCaseNotSupportedIsValid
                        }
                    />
                );
            })}
            {isConsentRequired && (
                <div className="u-spacing-stack-top-6">
                    <CheckboxField
                        id="edi-consent-checkbox"
                        checked={isConsentChecked}
                        onChange={(checkBoxEvent) => {
                            setIsConsentChecked(checkBoxEvent.target.checked);
                            setFieldValidationFlags({
                                ...fieldValidationFlags,
                                isConsentFieldIsValid: checkBoxEvent.target.checked
                            });
                        }}
                        label={
                            <div className={'paragraph--small u-spacing-stack-bottom-none'}>
                                <StringParagraph className="paragraph--small u-spacing-stack-s">
                                    {isCVR
                                        ? getHippoText(
                                              'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT-CVR'
                                          )
                                        : getHippoText(
                                              'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT'
                                          )}
                                </StringParagraph>
                                <Link
                                    onClick={(e) => {
                                        setShowConsentModal(true);
                                        e.preventDefault();
                                    }}
                                >
                                    <StringParagraph className="paragraph--small u-spacing-stack-s">
                                        {getHippoText(
                                            isCVR
                                                ? 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CVR-CONSENT-LINK'
                                                : 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT-LINK'
                                        )}
                                    </StringParagraph>
                                </Link>
                            </div>
                        }
                        isValid={
                            fieldValidationFlags.isConsentFieldIsValid === undefined ||
                            fieldValidationFlags.isConsentFieldIsValid
                        }
                    />
                    <CheckboxField
                        id="edi-consent-checkbox2"
                        checked={isConsentChecked2}
                        onChange={(checkBoxEvent) => {
                            setIsConsentChecked2(checkBoxEvent.target.checked);
                            setFieldValidationFlags({
                                ...fieldValidationFlags,
                                isConsentField2IsValid: checkBoxEvent.target.checked
                            });
                        }}
                        label={
                            <div className={'paragraph--small u-spacing-stack-bottom-none'}>
                                <StringParagraph className="paragraph--small u-spacing-stack-s">
                                    {getHippoText('DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT2')}
                                </StringParagraph>
                                <Link
                                    onClick={(e) => {
                                        setShowConsent2Modal(true);
                                        e.preventDefault();
                                    }}
                                >
                                    <StringParagraph className="paragraph--small u-spacing-stack-s">
                                        {getHippoText(
                                            isCVR
                                                ? 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CVR-CONSENT2-LINK'
                                                : 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT2-LINK'
                                        )}
                                    </StringParagraph>
                                </Link>
                            </div>
                        }
                        isValid={
                            fieldValidationFlags.isConsentField2IsValid === undefined ||
                            fieldValidationFlags.isConsentField2IsValid
                        }
                    />
                </div>
            )}
            {showModal && viewInformationModalContent && (
                <ViewInformationModal
                    showModal={showModal}
                    modalProps={viewInformationModalContent}
                    closeModal={() => {
                        setShowModal(false);
                    }}
                />
            )}
            {showDifferentPolicyHolderModal && (
                <DifferentPolicyHolderModal
                    isOpen={showDifferentPolicyHolderModal}
                    closeModal={() => {
                        setShowDifferentPolicyHolderModal(false);
                    }}
                />
            )}
            {showConsentModal && (
                <ViewHippoInformationModal
                    showModal={showConsentModal}
                    modalProps={{
                        title: isCVR
                            ? 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CVR-CONSENT-MODAL-TITLE'
                            : 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT-MODAL-TITLE',
                        description: isCVR
                            ? 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CVR-CONSENT-MODAL-DESCRIPTION'
                            : 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT-MODAL-DESCRIPTION'
                    }}
                    closeModal={() => {
                        setShowConsentModal(false);
                    }}
                />
            )}
            {showConsent2Modal && (
                <ViewHippoInformationModal
                    showModal={showConsent2Modal}
                    modalProps={{
                        title: isCVR
                            ? 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CVR-CONSENT2-MODAL-TITLE'
                            : 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT2-MODAL-TITLE',
                        description: isCVR
                            ? 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CVR-CONSENT2-MODAL-DESCRIPTION'
                            : 'DSS-EFFECTIVE-DATE-AND-CANCELLATION-CONSENT2-MODAL-DESCRIPTION'
                    }}
                    closeModal={() => {
                        setShowConsent2Modal(false);
                    }}
                />
            )}
            {youngPet && (
                <CallMeModal
                    activityCode="dss_call_request"
                    showModal={!!youngPet}
                    closeModal={() => {
                        setYoungPet(undefined);
                    }}
                    title={getHippoText('DSS-CALL-ME-MODAL-OLDER-OR-YOUNGER-PET-HEADER')}
                    description={getHippoText('DSS-CALL-ME-MODAL-YOUNGER-PET-SUBHEADER', {
                        name: youngPet
                    })}
                />
            )}
            {showMissingLicensePlatesModal && (
                <CallMeModal
                    activityCode="dss_call_request"
                    showModal={showMissingLicensePlatesModal}
                    closeModal={() => {
                        setShowMissingLicensePlatesModal(false);
                    }}
                    description={getHippoText('DSS-CALL-ME-MODAL-MISSING-LICENSE-PLATES-SUBHEADER')}
                />
            )}
        </AccordionWizardTemplate>
    );
};

export default DatesAndCancellationStep;
